import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetTipMetadataExcelQuery } from "../../libs/redux/slices/Trkgfile/trkgfileApislice";
import { describirPuerto } from "./puertos";

/** Funcion de Genio (GEST0004.METOPXXX) que lista los puertos de Colombia */
export const FUNCION_PUERTOS_COL = "f_getPuertoCol";

const FILTRO_METADATOS_PUERTO = {
  filters: `filters[REGESTXX]=ACTIVO&filters[METTIPXX]=LISTASELECCIONFUNCION&filters[METOPXXX]=${FUNCION_PUERTOS_COL}`,
};

/**
 * Los puertos de Colombia son ~33 registros de CIPO0170, asi que se piden una
 * sola vez y se comparten entre todas las tarjetas y el modal de metadatos.
 *
 * Va con fetch directo y NO con cimaxxxxApiSlice a proposito, igual que la
 * consulta de la lista de chequeo: el baseQueryWithLogOut de ese slice despacha
 * signOut() ante cualquier 401, y el nombre del puerto es solo un complemento
 * que no debe cerrar la sesion del visor. Si falla se olvida la promesa para
 * volver a intentarlo en la siguiente carga.
 */
let cargaPuertos = null;
const cargarPuertosCol = (tokenCima) => {
  if (!cargaPuertos) {
    cargaPuertos = fetch(
      `${process.env.REACT_APP_CIMA_ENDPOINT}/Registxx/Tracvuce/getPuertoEmbarqueByPais/CO`,
      {
        headers: {
          Accept: "application/json",
          Modulecima: "Registxx",
          ...(tokenCima ? { Authorization: `Bearer ${tokenCima}` } : {}),
        },
      }
    )
      .then((respuesta) => {
        if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
        return respuesta.json();
      })
      .then((cuerpo) => {
        const nombres = {};
        (cuerpo?.data ?? []).forEach((puerto) => {
          if (puerto?.id !== null && puerto?.id !== undefined) {
            nombres[String(puerto.id).trim()] = puerto.label;
          }
        });
        return nombres;
      })
      .catch(() => {
        cargaPuertos = null;
        return null;
      });
  }
  return cargaPuertos;
};

/**
 * Descripcion de los metadatos de puertos de Colombia para el visor.
 *
 * Los metadatos se reconocen por la funcion (METOPXXX = f_getPuertoCol) y no
 * por su METIDXXX, porque la misma funcion se puede usar en varios metadatos.
 * La consulta de documentos no cambia: la lista de metadatos de la funcion y la
 * de puertos se piden aparte, una vez por sesion, y la de puertos solo si algun
 * documento cargado tiene uno de esos metadatos.
 *
 * @param {Array<object>} rows - documentos cargados en el visor
 * @returns {{describirMetadato: function(string, *): (string|null), etiquetasPuerto: Map<string, string>}}
 *   describirMetadato devuelve CODIGO - NOMBRE, o null si la clave no es de
 *   puertos o los nombres aun no estan; etiquetasPuerto es METIDXXX -> METDESXX
 */
export const usePuertosCol = (rows = []) => {
  const tokenCima = useSelector((state) => state.cimaxxxx?.token);
  const { data } = useGetTipMetadataExcelQuery(FILTRO_METADATOS_PUERTO);
  const [nombresPorCodigo, setNombresPorCodigo] = useState(null);

  const etiquetasPuerto = useMemo(
    () => new Map((data?.data ?? []).map((metadato) => [metadato.METIDXXX, metadato.METDESXX])),
    [data]
  );

  const hayPuertos = useMemo(
    () =>
      etiquetasPuerto.size > 0 &&
      rows.some((row) =>
        Object.keys(row?.JSONMETX || {}).some((clave) => etiquetasPuerto.has(clave))
      ),
    [rows, etiquetasPuerto]
  );

  useEffect(() => {
    if (!hayPuertos || nombresPorCodigo) return;
    let vigente = true;
    cargarPuertosCol(tokenCima).then((nombres) => {
      if (vigente && nombres) setNombresPorCodigo(nombres);
    });
    return () => {
      vigente = false;
    };
  }, [hayPuertos, nombresPorCodigo, tokenCima]);

  const describirMetadato = useCallback(
    (clave, valor) => {
      if (!nombresPorCodigo || !etiquetasPuerto.has(clave)) return null;
      return describirPuerto(valor, nombresPorCodigo);
    },
    [nombresPorCodigo, etiquetasPuerto]
  );

  return { describirMetadato, etiquetasPuerto };
};
